import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useFileStore, RecentFilesPanel } from '@/storage';
import { useOnboardingStore } from '@/onboarding';

/**
 * AI Assistant feature icon
 */
function AISparkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  );
}

export function HomePage() {
  const navigate = useNavigate();
  const {
    setImportDialogOpen,
    setSavedModelsDialogOpen,
    newDocument,
  } = useFileStore();

  const handleNewModel = () => {
    newDocument();
    void navigate('/editor');
  };

  const handleImport = () => {
    setImportDialogOpen(true);
  };

  const handleOpenExamples = () => {
    void navigate('/examples');
  };

  const handleOpenSaved = () => {
    setSavedModelsDialogOpen(true);
  };

  return (
    <div className="min-h-[calc(100dvh-3.5rem)] overflow-x-hidden">
      <div className="container px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-6xl">Envelop</h1>
          <p className="mt-6 text-lg leading-8 text-muted-foreground">
            A modern, web-based EnergyPlus editor designed to democratize building energy modeling.
            Create, edit, and simulate EnergyPlus models directly in your browser.
          </p>
          <div className="mt-10 flex items-center justify-center gap-x-6">
            <Link to="/editor">
              <Button size="lg">IDF Editor</Button>
            </Link>
            <Link to="/hvac">
              <Button size="lg" variant="outline">HVAC Designer</Button>
            </Link>
            <button
              onClick={() => { useOnboardingStore.getState().startTour('main'); }}
              className="text-sm font-semibold leading-6 text-muted-foreground hover:text-foreground"
            >
              Take a tour
            </button>
            <a
              href="https://github.com/samuelduchesne/envelop"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold leading-6 text-muted-foreground hover:text-foreground"
            >
              Learn more <span aria-hidden="true">&rarr;</span>
            </a>
          </div>
        </div>

        {/* Quick actions */}
        <div className="mx-auto mt-16 max-w-4xl">
          <h2 className="mb-6 text-center text-2xl font-bold">Get Started</h2>
          <div className="grid gap-4 md:grid-cols-4">
            <div data-onboarding="quick-action-new">
              <QuickActionCard
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="12" y1="18" x2="12" y2="12" />
                    <line x1="9" y1="15" x2="15" y2="15" />
                  </svg>
                }
                title="New Model"
                description="Start with a blank template"
                onClick={handleNewModel}
              />
            </div>
            <QuickActionCard
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              }
              title="Import IDF"
              description="Open an existing file"
              onClick={handleImport}
            />
            <QuickActionCard
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
              }
              title="Saved Models"
              description="Open a saved project"
              onClick={handleOpenSaved}
            />
            <QuickActionCard
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              }
              title="Example Library"
              description="Browse and learn from examples"
              onClick={handleOpenExamples}
            />
          </div>
        </div>

        {/* AI Assistant Feature Highlight */}
        <div className="mx-auto mt-16 max-w-4xl">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-900/50 via-indigo-900/50 to-purple-900/50 border border-blue-700/30 p-8">
            {/* Background glow effect */}
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl" />

            <div className="relative">
              {/* Header with icon */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
                  <AISparkIcon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-bold text-white">AI Assistant</h2>
                    <span className="px-2 py-0.5 text-xs font-medium bg-green-500/20 text-green-400 rounded-full border border-green-500/30">
                      Powered by Claude
                    </span>
                  </div>
                  <p className="text-blue-200">Intelligent EnergyPlus modeling companion</p>
                </div>
              </div>

              {/* Main description */}
              <p className="text-gray-300 mb-8 text-lg leading-relaxed">
                Our AI Assistant has <span className="text-white font-semibold">complete knowledge of your IDF model</span> through
                a sophisticated Model Context Protocol (MCP) integration. It can read, query, modify, validate, and simulate
                your EnergyPlus model through natural conversation.
              </p>

              {/* MCP Tool Categories Grid */}
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-blue-300 uppercase tracking-wider mb-4">Full Model Knowledge Graph</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <MCPToolCategory
                    icon={
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    }
                    title="Read"
                    count={11}
                    description="Model summaries, objects, zones, HVAC systems"
                  />
                  <MCPToolCategory
                    icon={
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    }
                    title="Query"
                    count={6}
                    description="Search, find references, compare objects"
                  />
                  <MCPToolCategory
                    icon={
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    }
                    title="Write"
                    count={7}
                    description="Add, modify, rename, duplicate objects"
                  />
                  <MCPToolCategory
                    icon={
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    }
                    title="Validate"
                    count={9}
                    description="Schema validation, sanity checks, auto-fixes"
                  />
                  <MCPToolCategory
                    icon={
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    }
                    title="Simulate"
                    count={9}
                    description="Run simulations, monitor progress, analyze results"
                  />
                </div>
              </div>

              {/* Example prompts */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-blue-300 uppercase tracking-wider mb-3">Try asking</h3>
                <div className="flex flex-wrap gap-2">
                  <ExamplePromptPill>"Give me a summary of this model"</ExamplePromptPill>
                  <ExamplePromptPill>"What zones are in my building?"</ExamplePromptPill>
                  <ExamplePromptPill>"Check for any issues"</ExamplePromptPill>
                  <ExamplePromptPill>"Run a simulation and show me results"</ExamplePromptPill>
                </div>
              </div>

              {/* Feature highlights */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-white text-sm">Real-time Streaming</div>
                    <div className="text-xs text-gray-400">See responses as they're generated</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
                  <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-white text-sm">Live Tool Execution</div>
                    <div className="text-xs text-gray-400">Watch tools execute in real-time</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-white text-sm">EnergyPlus Expert</div>
                    <div className="text-xs text-gray-400">Deep knowledge of E+ modeling</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent files and features side by side */}
        <div className="mx-auto mt-16 max-w-5xl grid gap-8 md:grid-cols-3">
          {/* Recent files */}
          <div className="md:col-span-1 min-w-0">
            <RecentFilesPanel onOpenFile={() => navigate('/editor')} />
          </div>

          {/* Features */}
          <FeaturesSection />
        </div>
      </div>
    </div>
  );
}

interface QuickActionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}

function QuickActionCard({ icon, title, description, onClick }: QuickActionCardProps) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center rounded-lg border bg-card p-6 text-center shadow-sm transition-all hover:shadow-md hover:border-primary"
    >
      <div className="mb-3 text-primary">{icon}</div>
      <h3 className="font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
    </button>
  );
}

const FEATURES = [
  {
    title: 'Zero Installation',
    description: 'Pure web application that runs in any modern browser. No software to download or install.',
    details: [
      'Works on Windows, Mac, Linux, and Chromebooks without setup',
      'All processing happens locally in your browser—your files stay private',
      'Automatic updates without downloads or IT involvement',
      'Export and import IDF files to move work between devices',
    ],
  },
  {
    title: 'Visual HVAC Design',
    description: 'Node-graph editor for air-side systems that eliminates error-prone text-based node connections.',
    details: [
      'Drag-and-drop interface for building air handling units and zone equipment',
      'Automatic node naming and connection management',
      'Bi-directional sync keeps the visual graph and IDF text in sync',
      'Component palette with all standard EnergyPlus HVAC objects',
    ],
  },
  {
    title: 'Plant Loop Editor',
    description: 'Visual editor for hydronic systems including chilled water, hot water, and condenser loops.',
    details: [
      'Design supply and demand side equipment with drag-and-drop',
      'Supports chillers, boilers, cooling towers, pumps, and more',
      'Same bi-directional sync as the HVAC editor',
      'Clear visualization of water-side system topology',
    ],
  },
  {
    title: 'Visual Schedule Editor',
    description: 'Intuitive schedule creation with day, week, and year views for time-varying inputs.',
    details: [
      'Day schedules with hourly value editing and visualization',
      'Week schedules that assign day schedules to each day of the week',
      'Year schedules for seasonal variations and holidays',
      'Template library with common occupancy, lighting, and setpoint patterns',
    ],
  },
  {
    title: 'Intelligent Validation',
    description: 'Real-time error detection with contextual fix suggestions as you type.',
    details: [
      'Schema validation against the official EnergyPlus IDD',
      'Reference checking for object names and node connections',
      'Inline error markers in the code editor with explanations',
      'Quick-fix suggestions for common issues',
    ],
  },
  {
    title: '3D Geometry Viewer',
    description: 'Interactive 3D visualization of building geometry, zones, and surfaces.',
    details: [
      'Orbit, pan, and zoom to inspect building geometry from any angle',
      'Color-coded zones and surface types for easy identification',
      'Selection and highlighting of individual surfaces',
      'Useful for verifying geometry before simulation',
    ],
  },
  {
    title: 'Weather File Library',
    description: 'Browse and search thousands of weather files from locations around the world.',
    details: [
      'Access to TMY, TMY2, and TMY3 weather data',
      'Interactive map-based location selection',
      'Climate zone filtering by ASHRAE classification',
      'Design day data included from climate.onebuilding.org',
    ],
  },
  {
    title: 'Browser Simulation',
    description: 'Run EnergyPlus simulations directly in your browser using WebAssembly.',
    details: [
      'Full EnergyPlus engine compiled to run in modern browsers',
      'No server uploads—simulation runs locally on your machine',
      'Progress tracking and error reporting during simulation',
      'Results available immediately for analysis',
    ],
  },
  {
    title: 'HVAC Templates',
    description: 'Pre-built system templates for common HVAC configurations to speed up modeling.',
    details: [
      'One-click generation of VAV, packaged units, VRF, and more',
      'Automatically creates air loops, plant loops, and connections',
      'Templates follow EnergyPlus best practices',
      'Good starting point for custom system design',
    ],
  },
  {
    title: 'Results Dashboard',
    description: 'Visualize simulation results with interactive charts and data exports.',
    details: [
      'Time-series charts for energy consumption and temperatures',
      'Monthly and annual summary tables',
      'Support for ESO, MTR, and SQL output formats',
      'Export data to CSV for further analysis',
    ],
  },
];

function FeaturesSection() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <div className="md:col-span-2 min-w-0">
      <h2 className="mb-6 text-2xl font-bold">Key Features</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        {FEATURES.map((feature, index) => (
          <FeatureCard
            key={feature.title}
            title={feature.title}
            description={feature.description}
            details={feature.details}
            isExpanded={expandedIndex === index}
            onToggle={() => handleToggle(index)}
          />
        ))}
      </div>
    </div>
  );
}

interface FeatureCardProps {
  title: string;
  description: string;
  details?: string[];
  isExpanded: boolean;
  onToggle: () => void;
}

function FeatureCard({ title, description, details, isExpanded, onToggle }: FeatureCardProps) {
  return (
    <div
      className="rounded-lg border bg-card p-6 shadow-sm transition-all hover:shadow-md cursor-pointer"
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      aria-expanded={isExpanded}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-semibold">{title}</h3>
        <svg
          className={`w-4 h-4 flex-shrink-0 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{description}</p>
      {isExpanded && details && details.length > 0 && (
        <ul className="mt-4 space-y-2 text-sm text-muted-foreground border-t pt-4">
          {details.map((detail, index) => (
            <li key={index} className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>{detail}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * MCP Tool Category card for the AI section
 */
interface MCPToolCategoryProps {
  icon: React.ReactNode;
  title: string;
  count: number;
  description: string;
}

function MCPToolCategory({ icon, title, count, description }: MCPToolCategoryProps) {
  return (
    <div className="group relative p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-default">
      <div className="flex items-center gap-2 mb-1">
        <div className="text-blue-400">{icon}</div>
        <span className="font-medium text-white text-sm">{title}</span>
        <span className="text-xs text-blue-300 bg-blue-500/20 px-1.5 py-0.5 rounded-full">{count}</span>
      </div>
      <p className="text-xs text-gray-400 leading-snug">{description}</p>
    </div>
  );
}

/**
 * Example prompt pill for the AI section
 */
function ExamplePromptPill({ children }: { children: React.ReactNode }) {
  return (
    <span className="px-3 py-1.5 text-sm bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-gray-300 transition-colors cursor-default">
      {children}
    </span>
  );
}
